// ============================================================
// MySQLQuiz.js
//
// MySQL Knowledge Quiz
// Covers: Core SQL, Joins, Indexes, Transactions, Security,
// Stored Procedures, Window Functions, Backup & Recovery
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    LinearProgress,
    Divider,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

/* ---------------- BRAND ---------------- */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.05)",
    gold: "#DAA520",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
};

/* ---------------- QUESTIONS ---------------- */
const QUESTIONS = [
    {
        question: "What does ACID stand for in MySQL transactions?",
        options: [
            "Atomicity, Consistency, Isolation, Durability",
            "Accuracy, Control, Integrity, Distribution",
            "Availability, Consistency, Isolation, Dependency",
            "Atomicity, Caching, Indexing, Durability",
        ],
        answer: 0,
    },
    {
        question: "Which SQL clause filters rows before aggregation?",
        options: ["WHERE", "HAVING", "ORDER BY", "GROUP BY"],
        answer: 0,
    },
    {
        question: "Which JOIN returns all rows from the left table?",
        options: ["INNER JOIN", "RIGHT JOIN", "LEFT JOIN", "CROSS JOIN"],
        answer: 2,
    },
    {
        question: "What does an index primarily improve?",
        options: [
            "Write speed",
            "Read/query performance",
            "Storage compression",
            "Transaction isolation",
        ],
        answer: 1,
    },
    {
        question: "Which command safely undoes a transaction?",
        options: ["COMMIT", "UNDO", "ROLLBACK", "RESET"],
        answer: 2,
    },
    {
        question: "Which MySQL object runs automatically on data change?",
        options: ["Stored Procedure", "Trigger", "View", "Index"],
        answer: 1,
    },
    {
        question: "What does ROW_NUMBER() do?",
        options: [
            "Counts rows in a table",
            "Ranks rows uniquely in a window",
            "Aggregates rows",
            "Filters duplicates",
        ],
        answer: 1,
    },
    {
        question: "Which user should NEVER be used by applications?",
        options: ["Read-only user", "Reporting user", "root", "Limited user"],
        answer: 2,
    },
    {
        question: "Which tool creates logical backups?",
        options: ["InnoDB", "mysqlbinlog", "mysqldump", "EXPLAIN"],
        answer: 2,
    },
    {
        question: "What does EXPLAIN help you understand?",
        options: [
            "Table structure",
            "Query execution plan",
            "Transaction locks",
            "User permissions",
        ],
        answer: 1,
    },
];

/* ---------------- MAIN COMPONENT ---------------- */
export default function MySQLQuiz() {
    const [current, setCurrent] = useState(0);
    const [selected, setSelected] = useState(null);
    const [score, setScore] = useState(0);
    const [finished, setFinished] = useState(false);

    const progress = ((current + 1) / QUESTIONS.length) * 100;

    const handleNext = () => {
        if (selected === QUESTIONS[current].answer) {
            setScore(prev => prev + 1);
        }

        setSelected(null);

        if (current + 1 < QUESTIONS.length) {
            setCurrent(prev => prev + 1);
        } else {
            setFinished(true);
        }
    };

    const passMark = Math.ceil(QUESTIONS.length * 0.7);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        background: BRAND.surface,
                        borderRadius: 3,
                        border: `1px solid ${BRAND.goldGlow}`,
                    }}
                >
                    {!finished ? (
                        <>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <SchoolIcon sx={{ color: BRAND.gold }} />
                                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700 }}>
                                    MySQL Certification Quiz
                                </Typography>
                            </Box>

                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    mb: 3,
                                    backgroundColor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { backgroundColor: BRAND.gold },
                                }}
                            />

                            <Typography sx={{ color: BRAND.white, mb: 2 }}>
                                {current + 1}. {QUESTIONS[current].question}
                            </Typography>

                            <RadioGroup
                                value={selected}
                                onChange={(e) => setSelected(Number(e.target.value))}
                            >
                                {QUESTIONS[current].options.map((opt, idx) => (
                                    <FormControlLabel
                                        key={idx}
                                        value={idx}
                                        control={<Radio sx={{ color: BRAND.gold }} />}
                                        label={
                                            <Typography sx={{ color: BRAND.muted }}>
                                                {opt}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </RadioGroup>

                            <Button
                                fullWidth
                                disabled={selected === null}
                                onClick={handleNext}
                                sx={{
                                    mt: 3,
                                    background: BRAND.gold,
                                    color: "#100720",
                                    fontWeight: 700,
                                    "&:hover": { background: "#e0b200" },
                                }}
                            >
                                {current + 1 === QUESTIONS.length ? "Finish Quiz" : "Next Question"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="h4" sx={{ color: BRAND.white, mb: 2 }}>
                                Quiz Completed
                            </Typography>

                            <Divider sx={{ mb: 3, borderColor: BRAND.goldGlow }} />

                            <Typography sx={{ color: BRAND.white, mb: 1 }}>
                                Score: {score} / {QUESTIONS.length}
                            </Typography>

                            {score >= passMark ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: "limegreen" }} />
                                    <Typography sx={{ color: "limegreen", fontWeight: 700 }}>
                                        Congratulations! You passed 🎉
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CancelIcon sx={{ color: "tomato" }} />
                                    <Typography sx={{ color: "tomato", fontWeight: 700 }}>
                                        You did not pass. Review the lessons and try again.
                                    </Typography>
                                </Box>
                            )}

                            <Typography sx={{ color: BRAND.muted, mt: 3 }}>
                                Passing score: {passMark} correct answers
                            </Typography>
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    );
}
